import React, { useCallback, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useProvided } from 'nonaction';
import { ImageAssetsContainer, TextContainer } from '../../Container';
import Previewer from './Previewer';
import Editor from './Editor';
import DragBar from './DragBar.js';
import 'github-markdown-css';
import useDrop from '../../Container/Hooks/useDrop.js';
import { isImageFile, isMarkdownFile, readMarkdownFiles } from '../../Lib/markdownFiles.js';
import { createImageAssetMap, resolveMarkdownImages } from '../../Lib/markdownImages.js';
import { persistImageFiles } from '../../Lib/imageAssetStore.js';

const Markdown = ({ className }) => {
  const [text, setText] = useProvided(TextContainer);
  const [imageAssets, setImageAssets] = useProvided(ImageAssetsContainer);
  const [isDrag, setDrag] = useState(false);
  const [startX, setStartX] = useState(0);
  const [width, setWidth] = useState(window.innerWidth / 2);
  const markdownRef = useRef(null);

  useEffect(() => {
    const container = markdownRef.current;
    const editorScroll = container && container.querySelector('.CodeMirror-scroll');
    const previewScroll = container && container.querySelector('.preview');
    if (!editorScroll || !previewScroll) return undefined;

    let syncing = false;
    let frame = null;
    const syncScroll = (source, target) => {
      if (syncing) return;
      const sourceMax = source.scrollHeight - source.clientHeight;
      const targetMax = target.scrollHeight - target.clientHeight;
      const ratio = sourceMax > 0 ? source.scrollTop / sourceMax : 0;
      syncing = true;
      target.scrollTop = ratio * Math.max(targetMax, 0);
      frame = window.requestAnimationFrame(() => {
        syncing = false;
        frame = null;
      });
    };
    const syncEditor = () => syncScroll(editorScroll, previewScroll);
    const syncPreview = () => syncScroll(previewScroll, editorScroll);

    editorScroll.addEventListener('scroll', syncEditor, { passive: true });
    previewScroll.addEventListener('scroll', syncPreview, { passive: true });
    return () => {
      editorScroll.removeEventListener('scroll', syncEditor);
      previewScroll.removeEventListener('scroll', syncPreview);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  const handleFiles = useCallback((files, onComplete = () => {}) => {
    const fileArray = Array.from(files || []);
    const markdownFile = fileArray.find(isMarkdownFile);
    if (!markdownFile) {
      const imageFiles = fileArray.filter(isImageFile);
      if (imageFiles.length > 0) {
        setImageAssets(previousAssets => ({
          ...previousAssets,
          ...createImageAssetMap(imageFiles)
        }));
        persistImageFiles(imageFiles);
      }
      onComplete();
      return;
    }

    readMarkdownFiles(
      fileArray,
      (content, markdownFile, imageFiles) => {
        setText(content);
        setImageAssets(createImageAssetMap(imageFiles, markdownFile));
        persistImageFiles(imageFiles, markdownFile, { replace: true });
        onComplete();
      },
      error => {
        onComplete();
        alert(error.message);
      }
    );
  }, [setImageAssets, setText]);
  const [uploading, isOver] = useDrop(markdownRef, handleFiles);
  const previewText = resolveMarkdownImages(text, imageAssets);
  // Partial fileText & text

  useEffect(() => {
    const handleMouseUp = () => setDrag(false);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);
  // The state `isDrag` must be false, when mouse up!
  // So we listen it in window! (Seems ugly, but it just works ha.)
  return (
    <div
      ref={markdownRef}
      style={{ opacity: isOver || uploading ? 0.5 : 1 }}
      className={className}
      onMouseMove={e => {
        if (!isDrag) return;
        const pageX = e.nativeEvent.pageX;
        setWidth(pageX - startX);
      }}
    >
      <Editor
        className="no-print"
        width={width}
        text={text}
        setText={setText}
      />
      <DragBar
        className="no-print"
        isDrag={isDrag}
        setDrag={setDrag}
        setStartX={setStartX}
      />
      <Previewer>{previewText}</Previewer>
    </div>
  );
};

export default styled(Markdown)`
  * {
    box-sizing: border-box;
  }
  height: calc(100% - 40px);
  display: flex;
`;
